import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { HelpCircle, Plus, Pencil, Trash2, ArrowUp, ArrowDown, FolderPlus, Globe, Lock } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = { question: '', answer: '', category_id: '' };

function FAQManager({ audience }) {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catName, setCatName] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    const [f, c] = await Promise.all([
      axios.get(`${API}/faqs?audience=${audience}`),
      axios.get(`${API}/faq-categories?audience=${audience}`),
    ]);
    setFaqs(f.data || []);
    setCategories(c.data || []);
  }, [audience]);

  useEffect(() => { fetchAll().catch(() => toast.error('Failed to load FAQ')); }, [fetchAll]);

  // ----- questions -----
  const startAdd = (categoryId = '') => { setEditing(null); setForm({ ...EMPTY_FORM, category_id: categoryId }); setDialogOpen(true); };
  const startEdit = (f) => { setEditing(f); setForm({ question: f.question, answer: f.answer, category_id: f.category_id || '' }); setDialogOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { question: form.question, answer: form.answer, category_id: form.category_id || '' };
      if (editing) {
        await axios.put(`${API}/admin/faqs/${editing.id}`, payload);
        toast.success('Question updated');
      } else {
        await axios.post(`${API}/admin/faqs`, { ...payload, category_id: form.category_id || null, audience });
        toast.success('Question added');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete this question?\n\n"${f.question}"`)) return;
    try {
      await axios.delete(`${API}/admin/faqs/${f.id}`);
      toast.success('Question deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const moveFaq = async (items, idx, dir) => {
    const other = idx + dir;
    if (other < 0 || other >= items.length) return;
    const a = items[idx]; const b = items[other];
    try {
      await Promise.all([
        axios.put(`${API}/admin/faqs/${a.id}`, { order: b.order }),
        axios.put(`${API}/admin/faqs/${b.id}`, { order: a.order }),
      ]);
      fetchAll();
    } catch { toast.error('Failed to reorder'); }
  };

  // ----- categories -----
  const startAddCat = () => { setEditingCat(null); setCatName(''); setCatDialogOpen(true); };
  const startRenameCat = (c) => { setEditingCat(c); setCatName(c.name); setCatDialogOpen(true); };

  const saveCat = async () => {
    setSaving(true);
    try {
      if (editingCat) {
        await axios.put(`${API}/admin/faq-categories/${editingCat.id}`, { name: catName });
        toast.success('Category renamed');
      } else {
        await axios.post(`${API}/admin/faq-categories`, { name: catName, audience });
        toast.success('Category added');
      }
      setCatDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const removeCat = async (c, count) => {
    const msg = count > 0
      ? `Delete category "${c.name}"?\n\nIts ${count} question(s) will NOT be deleted — they move to General.`
      : `Delete category "${c.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/admin/faq-categories/${c.id}`);
      toast.success('Category deleted');
      fetchAll();
    } catch { toast.error('Failed to delete category'); }
  };

  const moveCat = async (idx, dir) => {
    const other = idx + dir;
    if (other < 0 || other >= categories.length) return;
    const a = categories[idx]; const b = categories[other];
    try {
      await Promise.all([
        axios.put(`${API}/admin/faq-categories/${a.id}`, { order: b.order }),
        axios.put(`${API}/admin/faq-categories/${b.id}`, { order: a.order }),
      ]);
      fetchAll();
    } catch { toast.error('Failed to reorder'); }
  };

  // ----- grouped view -----
  const generalItems = faqs.filter((f) => !f.category_id || !categories.some((c) => c.id === f.category_id));
  const sections = [
    ...categories.map((c, i) => ({ cat: c, idx: i, items: faqs.filter((f) => f.category_id === c.id) })),
    { cat: null, idx: -1, items: generalItems },
  ];

  const QuestionRow = ({ f, items, idx }) => (
    <Card className="rounded-sm border-border" data-testid={`admin-faq-row-${f.id}`}>
      <CardContent className="p-3 flex items-start gap-3">
        <div className="flex flex-col gap-0.5 flex-shrink-0 pt-1">
          <button onClick={() => moveFaq(items, idx, -1)} disabled={idx === 0} data-testid={`faq-move-up-${f.id}`} className="text-muted-foreground hover:text-primary disabled:opacity-20">
            <ArrowUp className="w-4 h-4" />
          </button>
          <button onClick={() => moveFaq(items, idx, 1)} disabled={idx === items.length - 1} data-testid={`faq-move-down-${f.id}`} className="text-muted-foreground hover:text-primary disabled:opacity-20">
            <ArrowDown className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">{f.question}</p>
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2 whitespace-pre-wrap">{f.answer}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="outline" size="icon" onClick={() => startEdit(f)} data-testid={`edit-faq-${f.id}`} className="rounded-sm h-8 w-8">
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => remove(f)} data-testid={`delete-faq-${f.id}`} className="rounded-sm h-8 w-8 text-red-400 hover:text-red-300">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
      <div className="space-y-6">
        <div className="border-b border-border pb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {audience === 'public'
              ? 'Shown to ANYONE on the login page — no account needed.'
              : 'Shown to logged-in employees and supervisors inside the portal.'}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={startAddCat} data-testid={`add-category-btn-${audience}`} className="rounded-sm">
              <FolderPlus className="w-4 h-4 mr-2" /> Add Category
            </Button>
            <Button onClick={() => startAdd('')} data-testid={`add-faq-btn-${audience}`} className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm">
              <Plus className="w-4 h-4 mr-2" /> Add Question
            </Button>
          </div>
        </div>

        {faqs.length === 0 && categories.length === 0 && (
          <Card className="rounded-sm border-border">
            <CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid="admin-faq-empty">
              No categories or questions yet — start with a category, then add questions.
            </CardContent>
          </Card>
        )}

        {sections.map(({ cat, idx, items }) => {
          if (!cat && items.length === 0) return null;
          return (
            <div key={cat ? cat.id : 'general'} className="space-y-2" data-testid={`admin-faq-section-${cat ? cat.id : 'general'}`}>
              <div className="flex items-center justify-between gap-2 border-l-2 border-primary pl-3">
                <h2 className="text-sm font-barlow tracking-widest uppercase text-primary">
                  {cat ? cat.name : 'General'}
                  <span className="ml-2 text-muted-foreground normal-case tracking-normal">({items.length})</span>
                </h2>
                {cat && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveCat(idx, -1)} disabled={idx === 0} data-testid={`cat-move-up-${cat.id}`} className="text-muted-foreground hover:text-primary disabled:opacity-20 p-1">
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => moveCat(idx, 1)} disabled={idx === categories.length - 1} data-testid={`cat-move-down-${cat.id}`} className="text-muted-foreground hover:text-primary disabled:opacity-20 p-1">
                      <ArrowDown className="w-4 h-4" />
                    </button>
                    <Button variant="ghost" size="icon" onClick={() => startRenameCat(cat)} data-testid={`rename-cat-${cat.id}`} className="h-7 w-7">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => removeCat(cat, items.length)} data-testid={`delete-cat-${cat.id}`} className="h-7 w-7 text-red-400 hover:text-red-300">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => startAdd(cat.id)} data-testid={`add-faq-in-${cat.id}`} className="h-7 text-xs text-muted-foreground hover:text-primary">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add here
                    </Button>
                  </div>
                )}
              </div>
              {items.length === 0 ? (
                <p className="text-xs text-muted-foreground pl-3">No questions in this category yet.</p>
              ) : (
                <div className="space-y-2">
                  {items.map((f, i) => <QuestionRow key={f.id} f={f} items={items} idx={i} />)}
                </div>
              )}
            </div>
          );
        })}

        {/* Question dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="rounded-sm sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-widest">{editing ? 'EDIT QUESTION' : 'ADD QUESTION'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category</Label>
                <Select value={form.category_id || 'general'} onValueChange={(v) => setForm({ ...form, category_id: v === 'general' ? '' : v })}>
                  <SelectTrigger className="rounded-sm" data-testid="faq-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General (no category)</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Question *</Label>
                <Input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} placeholder="e.g. How do I clock in?" data-testid="faq-question-input" className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Answer *</Label>
                <Textarea value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} rows={6} placeholder="Write the answer…" data-testid="faq-answer-input" className="rounded-sm" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={save} disabled={saving || !form.question.trim() || !form.answer.trim()} data-testid="save-faq-btn" className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm">
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Category dialog */}
        <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
          <DialogContent className="rounded-sm sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-widest">{editingCat ? 'RENAME CATEGORY' : 'ADD CATEGORY'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name *</Label>
              <Input value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="e.g. Clock In / Clock Out" data-testid="category-name-input" className="rounded-sm" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCatDialogOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={saveCat} disabled={saving || !catName.trim()} data-testid="save-category-btn" className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm">
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  );
}

export default function AdminFAQPage() {
  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-faq-page">
        <div>
          <h1 className="text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
            <HelpCircle className="w-6 h-6 text-primary" strokeWidth={1.5} /> FAQ MANAGEMENT
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Manage questions for the internal portal and the public login page.</p>
        </div>
        <Tabs defaultValue="internal" className="space-y-4">
          <TabsList className="rounded-sm">
            <TabsTrigger value="internal" data-testid="faq-tab-internal" className="rounded-sm">
              <Lock className="w-3.5 h-3.5 mr-2" /> Portal FAQ
            </TabsTrigger>
            <TabsTrigger value="public" data-testid="faq-tab-public" className="rounded-sm">
              <Globe className="w-3.5 h-3.5 mr-2" /> Login Page FAQ
            </TabsTrigger>
          </TabsList>
          <TabsContent value="internal">
            <FAQManager audience="internal" />
          </TabsContent>
          <TabsContent value="public">
            <FAQManager audience="public" />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
